import React, { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { describeWriteFailure } from '../utils/writeFailure';
import { GoogleLogin, googleLogout, useGoogleOneTapLogin } from '@react-oauth/google';
import './Profile.css';
import { getGoogleToken, storeGoogleToken, clearGoogleToken } from '../utils/googleAuth';
import useICalEvents from '../hooks/useICalEventsNew';
import { validateICalUrl } from '../utils/icalUtils';
import { useOutlookEvents } from '../hooks/useOutlookEvents';
import { isZoomConnected, disconnectZoom, getZoomUserInfo } from '../utils/zoomUtils';
import { startOAuth } from '../utils/oauthStart';
import { isTeamsConnected, getTeamsConnectionInfo, testTeamsConnection } from '../utils/teamsUtils';
import { isGmailAvailable } from '../utils/gmailUtils';
import { showErrorToast, showToast } from '../utils/toast';
import { formatDateTime } from '../utils/formatting';   // Block 233
import { messageFromError } from '../utils/apiError';
import {
  Avatar, Banner, Button, Card, Checkbox, Field, Input, PageHeader, PageLayout, Select, StatusPill, TabGroup, TabPanel, Textarea, HeaderButton, useRevealForm,
} from '../ui';
import './Profile.v2.css';

// Block K (2026-10-11): the /profile "Account" page on the v2 kit (Settings → Profile is a
// different file, ProfileSettings.jsx). No mockup: it follows the list and card patterns
// (VSC-BLOCKS UI-22). Every field, tab and integration it had is kept. Fixed, failing first in
// ProfilePage.test.js: "Save Changes" saved nothing to the account (name, phone and timezone
// now go to /api/users/me and are read back; the rest is labelled as kept on this device); the
// calendar feed read "Not connected" on a second device although it lives on the account; three
// buttons opened pages that do not exist. alert() boxes are toasts.
const DEFAULT_PROFILE = {
  name: "", email: "", bio: "", phone: "", website: "", location: "", timezone: "", avatar: "",
  socials: { twitter: "", linkedin: "", tiktok: "", instagram: "", facebook: "" },
  integrations: {
    google: null, notion: null, trello: null,
    ical: { url: "", connected: false, lastSync: null },
    outlook: { connected: false, userInfo: null, lastSync: null },
    zoom: { connected: false, userInfo: null, lastSync: null },
  },
  preferences: {
    theme: "light", notifications: true, emailReminders: true, defaultTaskDuration: 60,
    defaultMeetingType: "", // NEW: Default meeting type preference
    workingHours: { start: "09:00", end: "17:00" },
  },
};

// A stored copy from an older version may lack a section; fill it from the defaults so a
// missing one never breaks the page.
function withDefaults(stored) {
  const p = stored && typeof stored === 'object' ? stored : {};
  return {
    ...DEFAULT_PROFILE, ...p,
    socials: { ...DEFAULT_PROFILE.socials, ...(p.socials || {}) },
    integrations: { ...DEFAULT_PROFILE.integrations, ...(p.integrations || {}) },
    preferences: {
      ...DEFAULT_PROFILE.preferences, ...(p.preferences || {}),
      workingHours: { ...DEFAULT_PROFILE.preferences.workingHours, ...((p.preferences || {}).workingHours || {}) },
    },
  };
}

// The fields the ACCOUNT holds (the rest of this page lives in this browser only).
function fromAccount(profile, user) {
  if (!user) return profile;
  const pick = (k) => (user[k] !== undefined && user[k] !== null ? user[k] : profile[k]);
  return { ...profile, name: pick('name'), email: pick('email'), phone: pick('phone'), timezone: pick('timezone') };
}

const TIMEZONES = [
  ['', 'Select a timezone'], ['UTC', 'UTC'], ['America/New_York', 'Eastern Time'], ['America/Chicago', 'Central Time'],
  ['America/Denver', 'Mountain Time'], ['America/Los_Angeles', 'Pacific Time'], ['Europe/London', 'London'],
  ['Europe/Paris', 'Paris'], ['Asia/Tokyo', 'Tokyo'], ['Asia/Shanghai', 'Shanghai'],
];

// Opens a page of the app (App.js listens for aidey:navigate and maps the path to its view).
const openPage = (path) => window.dispatchEvent(new CustomEvent('aidey:navigate', { detail: path }));

function Profile() {
  // Added 2026-10-04: the iCal feed URL is saved to the ACCOUNT now, so this page
  // needs the current user in order to read the answer back.
  const { currentUser, setCurrentUser } = useCurrentUser();
  // Simplified Google OAuth availability check
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const isGoogleOAuthAvailable = !!(clientId && clientId.trim() && clientId !== 'your_actual_google_client_id_here');
  
  // Google token state - initialize from localStorage using utility
  const [googleToken, setGoogleTokenState] = useState(() => {
    return getGoogleToken();
  });

  // Profile state
  const [profile, setProfile] = useState(() => {
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem("userProfile") || "null"); } catch { stored = null; }
    return fromAccount(withDefaults(stored), currentUser);
  });
  // When the account changes under the page (a save, a refresh), its fields follow it.
  useEffect(() => {
    if (currentUser) setProfile((prev) => fromAccount(prev, currentUser));
  }, [currentUser?.name, currentUser?.email, currentUser?.phone, currentUser?.timezone]); // eslint-disable-line react-hooks/exhaustive-deps

  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  // Edit profile switches the fields on: bring the first one into view with the cursor in it
  // (Carla, 2026-10-21). Only on the tabs whose fields Edit switches on.
  const editRef = useRevealForm(isEditing);
  const editableTab = ['personal', 'socials', 'preferences'].includes(activeTab);
  const [saveState, setSaveState] = useState(null);   // { status: 'saving' | 'saved' | 'failed', text }

  // THE CASE (Block K): this button only switched editing off. The fields the account holds go
  // to it now, and are read back before the page says so.
  const saveProfile = async () => {
    const body = { phone: profile.phone || '' };
    if (String(profile.name || '').trim()) body.name = String(profile.name).trim();
    if (profile.timezone) body.timezone = profile.timezone;
    setSaveState({ status: 'saving' });
    try {
      const res = await api('/api/users/me', { method: 'PUT', body: JSON.stringify(body) });
      const data = await res.json();
      const u = data?.user;
      const kept = u && (body.name === undefined || u.name === body.name) && (body.timezone === undefined || u.timezone === body.timezone);
      if (!kept) {
        setSaveState({ status: 'failed', text: 'The account did not keep the change. Please try again.' });
        return;
      }
      if (setCurrentUser) setCurrentUser((prev) => ({ ...prev, name: u.name, phone: u.phone, timezone: u.timezone }));
      setIsEditing(false);
      setSaveState({ status: 'saved', text: 'Your name, phone and timezone are saved to your account. The other details are kept on this device.' });
    } catch (err) {
      setSaveState({ status: 'failed', text: messageFromError(err, 'Your changes were not saved.') });
    }
  };
  
  // The feed lives on the account (preferences?.icalUrl, 2026-10-04); the local copy is only a
  // fallback for a browser whose user has not refreshed yet. This page used to read the local
  // copy alone, so a second device said "Not connected" while the feed was live.
  const accountIcalUrl = currentUser?.preferences?.icalUrl || '';
  const icalConnected = !!accountIcalUrl || !!profile.integrations.ical?.connected;
  const icalFeedUrl = accountIcalUrl || profile.integrations.ical?.url || '';

  // iCal integration states
  const [icalUrl, setIcalUrl] = useState(profile.integrations.ical?.url || "");
  const [icalTesting, setIcalTesting] = useState(false);
  const [icalTestResult, setIcalTestResult] = useState(null);
  
  // Use iCal hook
  const { 
    events: icalEvents, 
    loading: icalLoading, 
    error: icalError, 
    testUrl, 
    refreshEvents: refreshIcalEvents 
  } = useICalEvents(icalConnected ? icalFeedUrl : null);

  // Use Outlook hook
  const {
    events: outlookEvents,
    isLoading: outlookLoading,
    isConnected: outlookConnected,
    error: outlookError,
    userInfo: outlookUserInfo,
    lastSync: outlookLastSync,
    connect: connectOutlook,
    disconnect: disconnectOutlook,
    refresh: refreshOutlookEvents,
    testConnection: testOutlookConnection
  } = useOutlookEvents();

  // Zoom integration states
  const [zoomConnected, setZoomConnected] = useState(() => isZoomConnected());
  const [zoomUserInfo, setZoomUserInfo] = useState(null);

  // Teams integration states
  const [teamsConnected, setTeamsConnected] = useState(() => isTeamsConnected());
  const [teamsConnectionInfo, setTeamsConnectionInfo] = useState(() => getTeamsConnectionInfo());

  // Check Zoom connection on component mount
  useEffect(() => {
    const checkZoomConnection = async () => {
      const connected = isZoomConnected();
      setZoomConnected(connected);
      
      if (connected) {
        try {
          const userInfo = await getZoomUserInfo();
          setZoomUserInfo(userInfo);
        } catch (error) {
          console.error('Failed to get Zoom user info:', error);
          setZoomConnected(false);
        }
      }
    };
    
    checkZoomConnection();
  }, []);

  // Save profile to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("userProfile", JSON.stringify(profile));
    window.dispatchEvent(new Event('aidey:onboardingProgress'));
  }, [profile]);

  // Sync Outlook connection state with profile
  useEffect(() => {
    if (outlookConnected !== profile.integrations.outlook?.connected) {
      setProfile(prev => ({
        ...prev,
        integrations: {
          ...prev.integrations,
          outlook: {
            connected: outlookConnected,
            userInfo: outlookUserInfo,
            lastSync: outlookLastSync
          }
        }
      }));
    }
  }, [outlookConnected, outlookUserInfo, outlookLastSync, profile.integrations.outlook?.connected]);

  // Sync Zoom connection state with profile
  useEffect(() => {
    if (zoomConnected !== profile.integrations.zoom?.connected) {
      setProfile(prev => ({
        ...prev,
        integrations: {
          ...prev.integrations,
          zoom: {
            connected: zoomConnected,
            userInfo: zoomUserInfo,
            lastSync: zoomConnected ? new Date().toISOString() : null
          }
        }
      }));
    }
  }, [zoomConnected, zoomUserInfo, profile.integrations.zoom?.connected]);

  // Sync Teams connection with Outlook connection
  useEffect(() => {
    const updateTeamsConnection = () => {
      const connected = isTeamsConnected();
      const connectionInfo = getTeamsConnectionInfo();
      
      setTeamsConnected(connected);
      setTeamsConnectionInfo(connectionInfo);
    };

    updateTeamsConnection();
    
    // Update Teams connection when Outlook connection changes
    if (outlookConnected) {
      updateTeamsConnection();
    }
  }, [outlookConnected]);

  // Check if Google token is available on component mount
  useEffect(() => {
    if (googleToken && !profile.integrations.google) {
      // Token exists but profile doesn't show connected state
      // This can happen on page refresh - try to decode the existing token
      try {
        const userInfo = JSON.parse(atob(googleToken.split('.')[1]));
        setProfile(prev => ({
          ...prev,
          integrations: {
            ...prev.integrations,
            google: {
              connected: true,
              email: userInfo.email,
              name: userInfo.name,
              token: googleToken,
              connectedAt: new Date().toISOString()
            }
          }
        }));
      } catch (error) {
        // Token is invalid, remove it
        clearGoogleToken();
        setGoogleTokenState(null);
      }
    }
  }, [googleToken, profile.integrations.google]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setProfile(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setProfile(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleGoogleSuccess = (credentialResponse) => {
    // Store the token for later use using utility function
    const token = credentialResponse.credential;
    storeGoogleToken(token);
    setGoogleTokenState(token);
    
    // Decode the JWT token to get user info
    const userInfo = JSON.parse(atob(token.split('.')[1]));
    
    setProfile(prev => ({
      ...prev,
      name: prev.name || userInfo.name,
      email: prev.email || userInfo.email,
      avatar: prev.avatar || userInfo.picture,
      integrations: {
        ...prev.integrations,
        google: {
          connected: true,
          email: userInfo.email,
          name: userInfo.name,
          token: token, // Store token in profile state as well
          connectedAt: new Date().toISOString()
        }
      }
    }));
  };

  const handleGoogleLogout = () => {
    googleLogout();
    // Clear stored token using utility function
    clearGoogleToken();
    setGoogleTokenState(null);
    
    setProfile(prev => ({
      ...prev,
      integrations: {
        ...prev.integrations,
        google: null
      }
    }));
  };

  // iCal Integration Handlers
  const handleIcalTest = async () => {
    if (!icalUrl.trim()) {
      setIcalTestResult({ success: false, error: "Please enter an iCal URL" });
      return;
    }

    const validation = validateICalUrl(icalUrl);
    if (!validation.valid) {
      setIcalTestResult({ success: false, error: validation.error });
      return;
    }

    setIcalTesting(true);
    setIcalTestResult(null);

    try {
      const result = await testUrl(icalUrl);
      setIcalTestResult(result);
    } catch (error) {
      setIcalTestResult({ success: false, error: error.message });
    } finally {
      setIcalTesting(false);
    }
  };

  // ── THE FEED URL NOW LIVES ON THE ACCOUNT (2026-10-04) ────────────────────
  //
  // It used to live ONLY in localStorage under `userProfile`, and three live readers
  // depended on it — App.js, Dashboard and TeamCalendar. So a customer subscribed a
  // calendar on their laptop and on any other device it was simply empty, with no
  // error, because nothing had failed.
  //
  // IT IS NOT IN CalendarConnection.calendarUrl, AND THAT MATTERS. That column looks
  // like the right home and is not: it holds a CalDAV COLLECTION url for an
  // apple/caldav connection, `email` and `password_enc` are NOT NULL beside it, and
  // lib/calendarPush.js:315 does a caldavPut AGAINST IT. Putting a read-only .ics
  // subscription feed there would point a calendar WRITER at it. It goes in
  // preferences, which is where every other per-account setting lives (companyName,
  // brandColor, replyTo) and which the frontend already receives on every auth call.
  const [icalSaveError, setIcalSaveError] = useState('');

  const handleIcalConnect = async () => {
    if (!icalTestResult?.success) {
      setIcalSaveError('Test the link first, so you know it works.');
      return;
    }
    setIcalSaveError('');

    try {
      const res = await api('/api/users/me', {
        method: 'PUT',
        body: JSON.stringify({ preferences: { icalUrl: icalUrl.trim() } }),
      });
      const data = await res.json();
      // Read the answer back: the connection is only real if the server kept it.
      const kept = data?.user?.preferences?.icalUrl;
      if (kept !== icalUrl.trim()) {
        setIcalSaveError('We could not save your calendar link to your account, so it would only work on this device. Please try again.');
        return;
      }
      if (setCurrentUser) setCurrentUser(u => ({ ...u, preferences: data.user.preferences }));
    } catch (err) {
      setIcalSaveError(describeWriteFailure(err, 'save your calendar link'));
      return;
    }

    // The local copy stays as a cache for this device's immediate render; the
    // account is now the source of truth.
    setProfile(prev => ({
      ...prev,
      integrations: {
        ...prev.integrations,
        ical: {
          url: icalUrl,
          connected: true,
          lastSync: new Date().toISOString()
        }
      }
    }));

    setIcalTestResult(null);
  };

  const handleIcalDisconnect = async () => {
    // Clearing it locally only would leave the feed live on every other device —
    // the same split that caused this, pointing the other way.
    setIcalSaveError('');
    try {
      const res = await api('/api/users/me', {
        method: 'PUT',
        body: JSON.stringify({ preferences: { icalUrl: '' } }),
      });
      const data = await res.json();
      if (setCurrentUser && data?.user?.preferences) setCurrentUser(u => ({ ...u, preferences: data.user.preferences }));
    } catch (err) {
      setIcalSaveError(describeWriteFailure(err, 'remove your calendar link'));
      return;
    }
    setProfile(prev => ({
      ...prev,
      integrations: {
        ...prev.integrations,
        ical: {
          url: "",
          connected: false,
          lastSync: null
        }
      }
    }));
    setIcalUrl("");
    setIcalTestResult(null);
  };

  // Outlook integration handlers
  const handleOutlookConnect = async () => {
    try {
      const result = await connectOutlook();
      if (result.success) {
        // Profile state will be updated automatically via useEffect
        console.log('Outlook connected successfully');
      } else {
        console.error('Outlook connection failed:', result.message);
      }
    } catch (error) {
      console.error('Error connecting to Outlook:', error);
    }
  };

  const handleOutlookDisconnect = async () => {
    try {
      await disconnectOutlook();
      setProfile(prev => ({
        ...prev,
        integrations: {
          ...prev.integrations,
          outlook: {
            connected: false,
            userInfo: null,
            lastSync: null
          }
        }
      }));
    } catch (error) {
      console.error('Error disconnecting from Outlook:', error);
    }
  };

  const handleOutlookTest = async () => {
    try {
      const result = await testOutlookConnection();
      if (result.success) {
        showToast(result.message, { type: 'success' });
      } else {
        showErrorToast(result.message);
      }
    } catch (error) {
      showErrorToast(`The test did not run: ${error.message}`);
    }
  };

  // Zoom integration handlers
  // The server builds the link and the one-use state (C1); the browser-built link (initiateZoomAuth)
  // carried a state the callback never issued and could not complete. A refusal is said at the button.
  const [zoomError, setZoomError] = useState('');
  const handleZoomConnect = async () => {
    setZoomError('');
    try {
      await startOAuth('zoom');
    } catch (error) {
      setZoomError(messageFromError(error, 'Zoom could not be connected.'));
    }
  };

  const handleZoomDisconnect = async () => {
    try {
      disconnectZoom();
      setZoomConnected(false);
      setZoomUserInfo(null);
      showToast('Zoom is disconnected.', { type: 'success' });
    } catch (error) {
      console.error('Failed to disconnect from Zoom:', error);
      showErrorToast('Failed to disconnect from Zoom. Please try again.');
    }
  };

  // Teams integration handlers
  const handleTeamsTest = async () => {
    try {
      const result = await testTeamsConnection();
      if (result.success) {
        showToast(result.message, { type: 'success' });
      } else {
        showErrorToast(result.message);
      }
    } catch (error) {
      showErrorToast(`The Teams test did not run: ${error.message}`);
    }
  };

  const editable = isEditing;
  const statusPill = (on, onText = 'Connected', offText = 'Not connected') => (
    <StatusPill tone={on ? 'success' : 'neutral'}>{on ? onText : offText}</StatusPill>
  );
  const soon = <StatusPill tone="neutral">Coming soon</StatusPill>;
  const tabs = [
    { id: 'personal', label: 'Personal info', color: 'pastel-blue' },
    { id: 'socials', label: 'Social links', color: 'pastel-pink' },
    { id: 'integrations', label: 'Integrations', color: 'pastel-mint' },
    { id: 'preferences', label: 'Preferences', color: 'pastel-yellow' },
  ];
  const socialFields = [
    ['twitter', '🐦 Twitter', 'https://twitter.com/username'],
    ['linkedin', '💼 LinkedIn', 'https://linkedin.com/in/username'],
    ['tiktok', '🎵 TikTok', 'https://tiktok.com/@username'],
    ['instagram', '📸 Instagram', 'https://instagram.com/username'],
    ['facebook', '👤 Facebook', 'https://facebook.com/username'],
  ];

  return (
    <PageLayout data-page="profile">
      <PageHeader
        eyebrow={{ label: 'Account', color: 'pastel-neutral' }}
        title="Profile"
        subline="Your details, the services you connect, and how Aidey behaves on this device."
        primary={editable
          ? <Button variant="confirm" data-form-submit onClick={saveProfile} disabled={saveState?.status === 'saving'} aria-busy={saveState?.status === 'saving' || undefined}>{saveState?.status === 'saving' ? 'Saving…' : 'Save changes'}</Button>
          : <HeaderButton primary onClick={() => { setSaveState(null); setIsEditing(true); }}>Edit profile</HeaderButton>}
      />

      <Card lift={false} className="prof-hero">
        <div className="prof-who">
          <Avatar name={profile.name || 'You'} src={profile.avatar || undefined} size={64} color="pastel-blue" />
          <div className="prof-who__text">
            <span className="prof-who__name">{profile.name || 'Your name'}</span>
            <span className="prof-muted prof-wrap">{profile.email || 'No email on the account'}</span>
            <span className="prof-muted">{profile.location || 'No location added'}</span>
          </div>
        </div>
      </Card>

      {saveState?.status === 'saved' && <Banner tone="success" onDismiss={() => setSaveState(null)}>{saveState.text}</Banner>}
      {saveState?.status === 'failed' && <div role="alert"><Banner tone="problem" onDismiss={() => setSaveState(null)}>{saveState.text}</Banner></div>}

      <TabGroup label="Profile sections" idPrefix="prof" tabs={tabs} value={activeTab} onChange={setActiveTab} />

      <TabPanel idPrefix="prof" tabId={activeTab}>
        <div ref={editableTab ? editRef : undefined}>
        {activeTab === "personal" && (
          <div className="prof-grid">
            <Card color="pastel-blue" label="Saved to your account" lift={false}>
              <div className="prof-form">
                <Field label="Full name">
                  <Input value={profile.name || ''} onChange={(e) => handleInputChange('name', e.target.value)} disabled={!editable} placeholder="Enter your full name" autoComplete="name" />
                </Field>
                <Field label="Email" hint="Your sign-in email.">
                  <Input type="email" value={profile.email || ''} disabled readOnly />
                </Field>
                <Field label="Phone number">
                  <Input type="tel" value={profile.phone || ''} onChange={(e) => handleInputChange('phone', e.target.value)} disabled={!editable} placeholder="Enter your phone number" autoComplete="tel" />
                </Field>
                <Field label="Timezone">
                  <Select value={profile.timezone || ''} onChange={(e) => handleInputChange('timezone', e.target.value)} disabled={!editable}
                    options={TIMEZONES.map(([value, label]) => ({ value, label }))} />
                </Field>
              </div>
            </Card>
            <Card color="pastel-neutral" label="Kept on this device" lift={false}>
              <div className="prof-form">
                <Field label="Website">
                  <Input type="url" value={profile.website || ''} onChange={(e) => handleInputChange('website', e.target.value)} disabled={!editable} placeholder="https://yourwebsite.com" />
                </Field>
                <Field label="Location">
                  <Input value={profile.location || ''} onChange={(e) => handleInputChange('location', e.target.value)} disabled={!editable} placeholder="City, country" />
                </Field>
                <Field label="Bio" className="prof-span">
                  <Textarea rows={4} value={profile.bio || ''} onChange={(e) => handleInputChange('bio', e.target.value)} disabled={!editable} placeholder="Tell us about yourself" />
                </Field>
              </div>
            </Card>
          </div>
        )}

        {activeTab === "socials" && (
          <Card color="pastel-pink" label="Social media links" meta="Kept on this device" lift={false}>
            <div className="prof-form">
              {socialFields.map(([key, label, placeholder]) => (
                <Field key={key} label={label}>
                  <Input type="url" value={profile.socials[key] || ''} onChange={(e) => handleInputChange(`socials.${key}`, e.target.value)} disabled={!editable} placeholder={placeholder} />
                </Field>
              ))}
            </div>
          </Card>
        )}

        {activeTab === "integrations" && (
          <div className="prof-stack">
            <section className="prof-section" aria-labelledby="prof-cal-h">
              <h2 id="prof-cal-h" className="prof-section__title">📅 Calendars and scheduling</h2>
              <div className="prof-cards">
                <Card data-integration="google" color="pastel-blue" label="Google Calendar" meta={statusPill(!!profile.integrations.google)} lift={false}>
                  <p className="prof-muted">Connect Google Calendar, Gmail, and Drive for seamless task integration.</p>
                  {!isGoogleOAuthAvailable ? (
                    <div className="prof-note">
                      <p>ℹ️ Google integration requires OAuth setup. Add your Google Client ID to enable this feature.</p>
                      <details>
                        <summary>How to set up Google OAuth</summary>
                        <ol>
                          <li>Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer">Google Cloud Console</a></li>
                          <li>Create a new project or select an existing one</li>
                          <li>Enable the Google Calendar API</li>
                          <li>Create OAuth 2.0 Client ID credentials</li>
                          <li>Add your domain to the authorised origins</li>
                          <li>Copy the Client ID to your .env file as REACT_APP_GOOGLE_CLIENT_ID</li>
                        </ol>
                      </details>
                    </div>
                  ) : !profile.integrations.google ? (
                    <div className="prof-stack prof-stack--tight">
                      {clientId === 'demo-client-id-for-testing' && <Banner tone="notice">🧪 Demo mode: sign-in will not work with demo credentials.</Banner>}
                      <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => showErrorToast('Google sign-in did not finish. Please try again.')} text="continue_with" shape="rectangular" />
                    </div>
                  ) : (
                    <div className="prof-stack prof-stack--tight">
                      <p className="prof-wrap">Connected as <strong>{profile.integrations.google.email}</strong></p>
                      <div className="prof-actions">
                        <Button variant="destructive" onClick={handleGoogleLogout} className="prof-disconnect" aria-label="Disconnect Google Calendar">Disconnect</Button>
                      </div>
                    </div>
                  )}
                </Card>

                <Card data-integration="outlook" color="pastel-periwinkle" label="Microsoft Outlook" meta={statusPill(outlookConnected)} lift={false}>
                  <p className="prof-muted">Connect your Microsoft 365 or Outlook calendar with full sync.</p>
                  {!outlookConnected ? (
                    <div className="prof-stack prof-stack--tight">
                      <div className="prof-actions"><Button variant="create" onClick={handleOutlookConnect} disabled={outlookLoading}>{outlookLoading ? 'Connecting…' : 'Connect Outlook'}</Button></div>
                      {outlookError && <p className="prof-error" role="alert">{outlookError}</p>}
                    </div>
                  ) : (
                    <div className="prof-stack prof-stack--tight">
                      <p className="prof-wrap">Connected as <strong>{outlookUserInfo?.displayName || outlookUserInfo?.userPrincipalName}</strong></p>
                      <p className="prof-muted">{outlookEvents.length} event{outlookEvents.length === 1 ? '' : 's'} loaded · Last sync: {outlookLastSync ? formatDateTime(outlookLastSync) : 'never'}</p>
                      {outlookError && <p className="prof-error" role="alert">{outlookError}</p>}
                      <div className="prof-actions">
                        <Button variant="tertiary" onClick={handleOutlookTest} disabled={outlookLoading}>{outlookLoading ? 'Testing…' : 'Test connection'}</Button>
                        <Button variant="tertiary" onClick={refreshOutlookEvents} disabled={outlookLoading}>{outlookLoading ? 'Syncing…' : 'Refresh'}</Button>
                        <Button variant="destructive" onClick={handleOutlookDisconnect} className="prof-disconnect" aria-label="Disconnect Outlook">Disconnect</Button>
                      </div>
                    </div>
                  )}
                </Card>

                <Card data-integration="ical" color="pastel-mint" label="iCal Calendar" meta={statusPill(icalConnected)} lift={false}>
                  <p className="prof-muted">Connect any calendar by its iCal link (Apple, Outlook and others).</p>
                  {!icalConnected ? (
                    <div className="prof-stack prof-stack--tight">
                      <div className="prof-inline">
                        <Field label="iCal link">
                          <Input type="url" placeholder="Paste your iCal link here (for example webcal://…)" value={icalUrl} onChange={(e) => setIcalUrl(e.target.value)} disabled={icalTesting} />
                        </Field>
                        <Button variant="secondary" onClick={handleIcalTest} disabled={icalTesting || !icalUrl.trim()}>{icalTesting ? 'Testing…' : 'Test'}</Button>
                      </div>
                      {icalTestResult && (icalTestResult.success ? (
                        <div className="prof-result">
                          <p>Found {icalTestResult.eventCount} event{icalTestResult.eventCount === 1 ? '' : 's'}.</p>
                          {icalTestResult.events?.length > 0 && (
                            <ul>{icalTestResult.events.slice(0, 3).map((event, idx) => <li key={idx}>{event.title} - {event.date}</li>)}</ul>
                          )}
                          <div className="prof-actions"><Button variant="confirm" onClick={handleIcalConnect}>Connect calendar</Button></div>
                        </div>
                      ) : <p className="prof-error" role="alert">{icalTestResult.error}</p>)}
                      {icalSaveError && <p className="prof-error" role="alert">{icalSaveError}</p>}
                    </div>
                  ) : (
                    <div className="prof-stack prof-stack--tight">
                      <p className="prof-wrap">Connected to <strong>{icalFeedUrl}</strong></p>
                      <p className="prof-muted">{icalEvents.length} event{icalEvents.length === 1 ? '' : 's'} loaded{profile.integrations.ical?.lastSync ? ` · Last sync: ${formatDateTime(profile.integrations.ical.lastSync)}` : ''}</p>
                      {icalError && <p className="prof-error" role="alert">{icalError}</p>}
                      {icalSaveError && <p className="prof-error" role="alert">{icalSaveError}</p>}
                      <div className="prof-actions">
                        <Button variant="tertiary" onClick={refreshIcalEvents} disabled={icalLoading}>{icalLoading ? 'Syncing…' : 'Refresh'}</Button>
                        <Button variant="destructive" onClick={handleIcalDisconnect} className="prof-disconnect" aria-label="Disconnect Apple Calendar">Disconnect</Button>
                      </div>
                    </div>
                  )}
                </Card>
              </div>
            </section>

            <section className="prof-section" aria-labelledby="prof-prod-h">
              <h2 id="prof-prod-h" className="prof-section__title">⚡ Productivity and task management</h2>
              <div className="prof-cards">
                <Card color="pastel-neutral" label="Notion" meta={soon} lift={false}><p className="prof-muted">Export tasks and notes to Notion databases.</p></Card>
                <Card color="pastel-neutral" label="Trello" meta={soon} lift={false}><p className="prof-muted">Sync tasks with Trello boards and cards.</p></Card>
              </div>
            </section>

            <section className="prof-section" aria-labelledby="prof-meet-h">
              <h2 id="prof-meet-h" className="prof-section__title">🎥 Meetings and video calls</h2>
              <div className="prof-cards">
                <Card color="pastel-yellow" label="Meeting preferences" meta="Kept on this device" lift={false}>
                  <Field label="Default meeting type" hint="Chosen for you when you add a task with a meeting.">
                    <Select value={profile.preferences.defaultMeetingType || ""} onChange={(e) => handleInputChange('preferences.defaultMeetingType', e.target.value)}
                      options={[
                        { value: '', label: 'None' }, { value: 'Zoom', label: '🎥 Zoom' }, { value: 'Google Meet', label: '📹 Google Meet' },
                        { value: 'Teams', label: '💼 Microsoft Teams' }, { value: 'Custom Link', label: '🔗 Custom link' }, { value: 'Address', label: '📍 Physical address' },
                      ]} />
                  </Field>
                </Card>

                <Card data-integration="zoom" color="pastel-blue" label="Zoom" meta={statusPill(zoomConnected)} lift={false}>
                  <p className="prof-muted">Direct integration with Zoom for automatic meeting links.</p>
                  {zoomConnected && zoomUserInfo && (
                    <p className="prof-wrap">Account: <strong>{zoomUserInfo.email || 'Zoom user'}</strong>{zoomUserInfo.first_name ? ` (${zoomUserInfo.first_name} ${zoomUserInfo.last_name || ''})` : ''}</p>
                  )}
                  <div className="prof-actions">
                    {zoomConnected
                      ? <Button variant="destructive" onClick={handleZoomDisconnect} className="prof-disconnect" aria-label="Disconnect Zoom">Disconnect</Button>
                      : <Button variant="create" onClick={handleZoomConnect}>Connect to Zoom</Button>}
                  </div>
                  {zoomError && <p className="prof-error" role="alert">{zoomError}</p>}
                </Card>

                <Card data-integration="meet" color="pastel-green" label="Google Meet" meta={statusPill(!!googleToken, 'Ready', 'Needs Google Calendar')} lift={false}>
                  <p className="prof-muted">Adds Google Meet links when you create tasks. Uses your Google Calendar connection.</p>
                  {googleToken && profile.integrations.google
                    ? <p className="prof-wrap">Ready, using <strong>{profile.integrations.google.email}</strong>.</p>
                    : <p className="prof-muted">Connect Google Calendar above first.</p>}
                </Card>

                <Card data-integration="teams" color="pastel-lilac" label="Microsoft Teams" meta={statusPill(teamsConnected, 'Ready', 'Needs Outlook')} lift={false}>
                  <p className="prof-muted">Creates Teams meetings automatically. Uses your Outlook connection.</p>
                  {teamsConnected && teamsConnectionInfo.userInfo
                    ? <p className="prof-wrap">Ready, using <strong>{teamsConnectionInfo.userInfo.email}</strong>.</p>
                    : <p className="prof-muted">Connect Outlook above first.</p>}
                  {teamsConnected && <div className="prof-actions"><Button variant="tertiary" onClick={handleTeamsTest}>Test Teams</Button></div>}
                </Card>
              </div>
            </section>

            <section className="prof-section" aria-labelledby="prof-mail-h">
              <h2 id="prof-mail-h" className="prof-section__title">📧 Email</h2>
              <div className="prof-cards">
                <Card color="pastel-mint" label="Aidey email assistant" meta={<StatusPill tone="success">Available</StatusPill>} lift={false}>
                  <p className="prof-muted">Sorts your email, drafts replies and pulls out actions. Works with Gmail and Outlook.</p>
                  <div className="prof-actions">
                    <Button variant="confirm" onClick={() => openPage('/emails')}>Open Email Dashboard</Button>
                    <Button variant="tertiary" onClick={() => openPage('/settings?section=email')}>Email connection settings</Button>
                  </div>
                </Card>
                <Card data-integration="gmail" color="pastel-coral" label="Gmail" meta={statusPill(!!googleToken, 'Ready', 'Needs Google Calendar')} lift={false}>
                  <p className="prof-muted">Read, send and search Gmail from Aidey.</p>
                  {googleToken && profile.integrations.google
                    ? <div className="prof-actions"><Button variant="secondary" onClick={() => openPage('/emails')}>📧 Open Gmail in Emails</Button></div>
                    : <p className="prof-muted">Connect Google Calendar above first.</p>}
                </Card>
                <Card color="pastel-neutral" label="Outlook email" meta={soon} lift={false}><p className="prof-muted">Microsoft Outlook email management.</p></Card>
              </div>
            </section>
          </div>
        )}

        {activeTab === "preferences" && (
          <Card color="pastel-yellow" label="App preferences" meta="Kept on this device" lift={false}>
            <div className="prof-form">
              <Field label="Theme">
                <Select value={profile.preferences.theme} onChange={(e) => handleInputChange('preferences.theme', e.target.value)} disabled={!editable}
                  options={[{ value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }, { value: 'auto', label: 'Auto' }]} />
              </Field>
              <Field label="Default task duration (minutes)">
                <Input type="number" inputMode="numeric" min="15" max="480" value={Number.isFinite(profile.preferences.defaultTaskDuration) ? profile.preferences.defaultTaskDuration : ''} onChange={(e) => handleInputChange('preferences.defaultTaskDuration', parseInt(e.target.value, 10))} disabled={!editable} />
              </Field>
              <Field label="Working hours start">
                <Input type="time" value={profile.preferences.workingHours.start} onChange={(e) => handleInputChange('preferences.workingHours', { ...profile.preferences.workingHours, start: e.target.value })} disabled={!editable} />
              </Field>
              <Field label="Working hours end">
                <Input type="time" value={profile.preferences.workingHours.end} onChange={(e) => handleInputChange('preferences.workingHours', { ...profile.preferences.workingHours, end: e.target.value })} disabled={!editable} />
              </Field>
              <div className="prof-span prof-checks">
                <Checkbox checked={profile.preferences.notifications} onChange={(v) => handleInputChange('preferences.notifications', v)} disabled={!editable} label="Enable push notifications" />
                <Checkbox checked={profile.preferences.emailReminders} onChange={(v) => handleInputChange('preferences.emailReminders', v)} disabled={!editable} label="Send email reminders" />
              </div>
            </div>
          </Card>
        )}
        </div>
      </TabPanel>
    </PageLayout>
  );
}

export default Profile;
